import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  ChatMessageReceivedEvent,
  ChatMessageDeletedEvent,
  ChatMessageEditedEvent,
  ParticipantsAddedEvent,
  ParticipantsRemovedEvent,
} from "@azure/communication-signaling";
import { ChatClient } from "@azure/communication-chat";
import { ChatMessage } from "../types/chat";

interface InfiniteMessagesData {
  pages: Array<{
    messages: ChatMessage[];
    iterator?: unknown;
  }>;
  pageParams: unknown[];
}

/**
 * Hook để xử lý tin nhắn realtime từ Azure Communication Services
 * Tự động cập nhật query cache khi có tin nhắn mới, sửa, hoặc xóa
 */
export const useRealtimeMessage = (chatClient?: ChatClient | null, threadId?: string) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!chatClient || !threadId) return;

    // Handler khi nhận tin nhắn mới
    const handleMessageReceived = (event: ChatMessageReceivedEvent) => {
      if (event.threadId !== threadId) return;

      const newMessage: ChatMessage = {
        content: {
          message: event.message,
          attachments: event.attachments || [],
        },
        createdOn: event.createdOn ?? new Date().toISOString(),
        id: event.id,
        sender: event.sender as unknown as ChatMessage["sender"],
        senderCommunicationIdentifier: event.sender as unknown as ChatMessage["senderCommunicationIdentifier"],
        senderDisplayName: event.senderDisplayName,
        sequenceId: event.id,
        type: event.type.toLowerCase() as ChatMessage["type"],
        version: event.version,
        metadata: event.metadata,
      };

      // Cập nhật infinite query cache
      queryClient.setQueryData<InfiniteMessagesData>(["infinite-messages", threadId], (oldData) => {
        if (!oldData?.pages || oldData.pages.length === 0) {
          return oldData;
        }

        const newPages = [...oldData.pages];
        const newestPage = newPages[0];

        const messageExists = newestPage.messages?.some((msg: ChatMessage) => msg.id === newMessage.id);

        if (messageExists) {
          return oldData;
        }

        newPages[0] = {
          ...newestPage,
          messages: [newMessage, ...(newestPage.messages || [])],
        };

        return {
          ...oldData,
          pages: newPages,
        };
      });

      // Refetch danh sách chat bên ngoài
      queryClient.invalidateQueries({ queryKey: ["get-chats"] });
    };

    // Handler khi tin nhắn bị xóa
    const handleMessageDeleted = (event: ChatMessageDeletedEvent) => {
      if (event.threadId !== threadId) return;

      queryClient.setQueryData<InfiniteMessagesData>(["infinite-messages", threadId], (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            messages: page.messages.filter((msg: ChatMessage) => msg.id !== event.id),
          })),
        };
      });
    };

    // Handler khi tin nhắn được chỉnh sửa
    const handleMessageEdited = (event: ChatMessageEditedEvent) => {
      if (event.threadId !== threadId) return;

      queryClient.setQueryData<InfiniteMessagesData>(["infinite-messages", threadId], (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg: ChatMessage) =>
              msg.id === event.id
                ? {
                    ...msg,
                    content: { ...msg.content, message: event.message },
                    editedOn: new Date().toISOString(),
                  }
                : msg,
            ),
          })),
        };
      });
    };

    // Handler khi có thành viên được thêm vào
    const handleParticipantsAdded = (event: ParticipantsAddedEvent) => {
      if (event.threadId !== threadId) return;

      // Invalidate messages query để tải lại và hiển thị tin nhắn hệ thống
      queryClient.invalidateQueries({ queryKey: ["infinite-messages", threadId] });
    };

    // Handler khi có thành viên bị xóa
    const handleParticipantsRemoved = (event: ParticipantsRemovedEvent) => {
      if (event.threadId !== threadId) return;

      // Invalidate messages query để tải lại và hiển thị tin nhắn hệ thống
      queryClient.invalidateQueries({ queryKey: ["infinite-messages", threadId] });
    };

    // Đăng ký lắng nghe các sự kiện
    chatClient.on("chatMessageReceived", handleMessageReceived);
    chatClient.on("chatMessageDeleted", handleMessageDeleted);
    chatClient.on("chatMessageEdited", handleMessageEdited);
    chatClient.on("participantsAdded", handleParticipantsAdded);
    chatClient.on("participantsRemoved", handleParticipantsRemoved);

    console.log("✅ Realtime message listeners registered for thread:", threadId);

    // Dọn dẹp
    return () => {
      chatClient.off("chatMessageReceived", handleMessageReceived);
      chatClient.off("chatMessageDeleted", handleMessageDeleted);
      chatClient.off("chatMessageEdited", handleMessageEdited);
      chatClient.off("participantsAdded", handleParticipantsAdded);
      chatClient.off("participantsRemoved", handleParticipantsRemoved);
      console.log("🔌 Realtime message listeners unregistered");
    };
  }, [chatClient, threadId, queryClient]);
};
